<script setup lang="ts">
import FormColorPicker from '@/components/ui/ColorPicker/ColorPicker.vue'
import ImageUploaderNew from '@/components/ui/ImageUploader/ImageUploaderNew.vue'
import { useVModel } from '@vueuse/core'

import useMedia from '@/composables/media'
import useSessionStorage from '@/composables/sessionStorage'
import ContestsPreview from '@/views/contests/Components/ContestsPreview.vue'
import texts from '@/views/contests/texts.json'
import { CONTEST_MECHANICS } from '@/views/contests/utils'

import type { DropdownValue } from '@/types/dropdown'
import type { ContestMechanic } from '@/types/global'
import type { Contest, ImageFile } from '@/types/types'
import type { Boost } from './types/global'

const props = defineProps<{
  getContest: Contest
  selectedBoost: Boost
  hasDefaultSettings: boolean
}>()

const hasDefaultSettings = useVModel(props, 'hasDefaultSettings')

const route = useRoute()

const { isEmptyDuplicateContestItem } = useSessionStorage()
const { isDefaultLogo } = useMedia()
const { getContrast } = useColor()
const { getPredominantColors } = useColor()
const { t } = useI18n()
const { merchantLogo, merchantName } = useLocalStorage()

const contest = ref<Contest>(props.getContest)
const logoImages = ref<ImageFile[]>([])
const coverImages = ref<ImageFile[]>([])
const primaryColor = ref<string>(contest.value.backgroundColor)
const isEditMode = ref(!!route.params?.contestId)
const options: (DropdownValue & { value: ContestMechanic })[] = CONTEST_MECHANICS.map((value, index) => ({
  id: index + 1,
  value,
  text: t(`pages.contests.contestsCreatePage.style.form.fields.mechanic.${value}`)
}))

const mechanic = ref(contest.value?.mechanic || 'fortuneWheel')
const selectedGameContestValue = computed(() => {
  return options.find((option) => option.value === mechanic.value)
})
const contestGameOption = {
  fortuneWheel: {
    logo: true,
    coverImage: true,
    primaryColor: true,
    contestGameRewardsColor: true,
    textColor: true
  },
  mysteryBox: {
    logo: true,
    coverImage: false,
    primaryColor: false,
    contestGameRewardsColor: false,
    textColor: false
  },
  scratchCard: {
    logo: true,
    coverImage: true,
    primaryColor: false,
    contestGameRewardsColor: true,
    textColor: false
  }
}

const noBoost = computed(() => {
  return { id: 'none', title: '', description: '' }
})

const logo = computed(() => {
  const logo = contest.value.logo || contest.value.logoUrl || merchantLogo.value
  const hideDefaultLogo = logo && isDefaultLogo(logo) && !isEditMode.value

  return hideDefaultLogo ? null : logo
})

const coverImg = computed(() => {
  return contest.value.coverImage ?? contest.value.coverImageUrl
})

const setDefaultColors = () => {
  const primaryColorContrast = getContrast(primaryColor.value)

  if (contestGameOption[mechanic.value].primaryColor) {
    contest.value.backgroundColor = primaryColor.value
    contest.value.buttonTextColor = primaryColor.value
    contest.value.textColor = primaryColorContrast
    contest.value.buttonColor = primaryColorContrast
    contest.value.contestGameRewards.forEach((contestGameReward, contestIndex) => {
      if (contestIndex % 2 === 0) {
        contestGameReward.color = primaryColorContrast
        contestGameReward.textColor = primaryColor.value
      } else {
        contestGameReward.color = primaryColor.value
        contestGameReward.textColor = primaryColorContrast
      }
    })
  } else {
    contest.value.backgroundColor = primaryColor.value
    contest.value.buttonColor = primaryColor.value
    contest.value.buttonTextColor = getContrast(primaryColor.value)
    contest.value.textColor = getContrast(primaryColor.value)
  }
}

const setDefaultWording = () => {
  contest.value.title = texts[mechanic.value].title
  contest.value.subtitle = texts[mechanic.value].subtitle
  contest.value.callToActionText = texts[mechanic.value].callToActionText
  contest.value.formTitle = texts[mechanic.value].formTitle
  contest.value.formSubtitle = texts[mechanic.value].formSubtitle
  contest.value.formSubmitButtonText = texts[mechanic.value].formSubmitButtonText
  contest.value.optinText = t('pages.contests.contestsCreatePage.preview.optinText', {
    merchantName: merchantName.value
  })
  contest.value.winnerText = texts[mechanic.value].winnerText
  contest.value.confirmationText = texts[mechanic.value].confirmationText
}

const updateSelectGameContest = (event) => {
  mechanic.value = options.find((option) => option.id === event).value as ContestMechanic
  contest.value.mechanic = mechanic.value

  setDefaultWording()
  setDefaultColors()
}

const imageUploaderLogoUpdate = async (e) => {
  if (e.length === 0) {
    contest.value.logo = null
    contest.value.logoUrl = null
    return
  }

  contest.value.logo = e[0].path

  if (!isEditMode.value) {
    try {
      const colors = await getPredominantColors(contest.value?.logo)
      primaryColor.value = colors?.primary
    } finally {
      setDefaultColors()
    }
  }
}

const imageUploaderCoverUpdate = (e) => {
  if (e.length === 0) {
    contest.value.coverImage = null
    contest.value.coverImageUrl = null
    return
  }
  contest.value.coverImage = e[0].path
}

onMounted(() => {
  if (!isEditMode.value && isEmptyDuplicateContestItem.value && !hasDefaultSettings.value) {
    setDefaultColors()

    hasDefaultSettings.value = true
  }

  if (logo.value) {
    logoImages.value = [
      {
        id: Date.now(),
        path: logo.value
      }
    ]
  }

  if (coverImg.value) {
    coverImages.value = [
      {
        id: Date.now() + 1,
        path: coverImg.value
      }
    ]
  }
})
</script>

<template>
  <div class="flex flex-col gap-5 xl:flex-row">
    <div class="xl:flex-1">
      <Card>
        <CardHeader>
          <CardTitle>{{ t('pages.contests.contestsCreatePage.style.title') }}</CardTitle>
          <CardDescription>{{ t('pages.contests.contestsCreatePage.style.description') }}</CardDescription>
        </CardHeader>
      </Card>

      <div class="mt-5">
        <BaseDisclosure
          is-default-open
          :title="t('pages.contests.contestsCreatePage.style.form.fields.gameType')"
          panel-class="!rounded-xl"
        >
          <div class="wrap-brick-dropdown flex w-full! flex-col gap-4 px-2 py-5 sm:px-8">
            <ComboboxSelect
              value-key="id"
              label-key="text"
              :model-value="selectedGameContestValue?.id"
              :placeholder="selectedGameContestValue?.text ?? 'Choisis le type de jeu'"
              :options="options"
              @update:model-value="updateSelectGameContest"
            />
          </div>
        </BaseDisclosure>
      </div>

      <div class="mt-5">
        <BaseDisclosure
          is-default-open
          :title="t('pages.contests.contestsCreatePage.preview.disclosure.branding')"
          panel-class="!rounded-xl"
        >
          <div class="flex w-full flex-col gap-4 px-2 py-5 sm:px-8">
            <div
              v-if="contestGameOption[mechanic].logo || contestGameOption[mechanic].coverImage"
              class="space-y-6 border-b pb-6 sm:mt-0 sm:space-y-5"
            >
              <div v-if="contestGameOption[mechanic].logo" class="mt-6 space-y-6 sm:mt-2 sm:space-y-5">
                <div class="sm:col-span-6">
                  <label class="block text-sm font-medium text-foreground">
                    {{ t('pages.contests.contestsCreatePage.style.form.fields.logo') }}
                  </label>
                  <div class="mt-3">
                    <ImageUploaderNew
                      v-model:images="logoImages"
                      :initial-images="[]"
                      :deleted-images="[]"
                      :single-image="true"
                      crop
                      @update:images="imageUploaderLogoUpdate"
                    />
                  </div>
                  <p class="mt-1 text-xs text-muted-foreground">
                    {{ t('ui.imageUploader.constraints') }}
                  </p>
                </div>
              </div>
              <div v-if="contestGameOption[mechanic].coverImage" class="sm:col-span-6">
                <label class="block text-sm font-medium text-foreground">
                  {{ t('pages.contests.contestsCreatePage.style.form.fields.coverImage') }}
                </label>
                <div class="mt-3">
                  <ImageUploaderNew
                    v-model:images="coverImages"
                    :initial-images="[]"
                    :deleted-images="[]"
                    :single-image="true"
                    crop
                    :aspect-ratio="mechanic === 'scratchCard' ? 1 : undefined"
                    @update:images="imageUploaderCoverUpdate"
                  />
                </div>
                <p class="mt-1 text-xs text-muted-foreground">
                  {{ t('ui.imageUploader.constraints') }}
                </p>
              </div>
            </div>
            <div class="sm:mt-2">
              <div>
                <label :for="`primary.color`">
                  <p class="text-sm font-medium text-foreground">
                    {{ t('pages.contests.contestsCreatePage.preview.primaryColor') }}
                  </p>
                  <p class="mt-1 text-sm text-muted-foreground">
                    {{ t('pages.contests.contestsCreatePage.preview.primaryColorDescription') }}
                  </p>
                </label>
                <div class="mt-4 flex h-12 w-max">
                  <FormColorPicker id="primary.color" v-model="primaryColor" @update:modelValue="setDefaultColors" />
                </div>
              </div>
            </div>
          </div>
        </BaseDisclosure>
      </div>

      <div class="mt-5">
        <BaseDisclosure
          :title="t('pages.contests.contestsCreatePage.preview.disclosure.colors')"
          panel-class="!rounded-xl"
        >
          <div class="flex w-full flex-col gap-4 rounded-b-lg border bg-white p-6 px-2 py-8 md:px-8">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label for="backgroundColor" class="block text-sm font-medium text-foreground">
                  {{ t('pages.contests.contestsCreatePage.style.form.fields.backgroundColor') }}
                </label>
                <div class="mt-3 sm:mt-0">
                  <div class="flex h-12 w-max">
                    <FormColorPicker id="backgroundColor" v-model="contest.backgroundColor" />
                  </div>
                </div>
              </div>
              <div v-if="contestGameOption[mechanic].textColor">
                <label for="textColor" class="block text-sm font-medium text-foreground">
                  {{ t('pages.contests.contestsCreatePage.style.form.fields.textsColor') }}
                </label>
                <div class="mt-1 sm:mt-0">
                  <div class="flex h-12 w-max">
                    <FormColorPicker id="textColor" v-model="contest.textColor" />
                  </div>
                </div>
              </div>

              <div>
                <label for="buttonColor" class="block text-sm font-medium text-foreground">
                  {{ t('pages.contests.contestsCreatePage.style.form.fields.buttonsColor') }}
                </label>
                <div class="mt-1 sm:mt-0">
                  <div class="flex h-12 w-max">
                    <FormColorPicker id="buttonColor" v-model="contest.buttonColor" />
                  </div>
                </div>
              </div>
              <div>
                <label for="buttonTextColor" class="block text-sm font-medium text-foreground">
                  {{ t('pages.contests.contestsCreatePage.style.form.fields.buttonsTextColor') }}
                </label>
                <div class="mt-1 sm:mt-0">
                  <div class="flex h-12 w-max">
                    <FormColorPicker id="buttonTextColor" v-model="contest.buttonTextColor" />
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="contestGameOption[mechanic].contestGameRewardsColor && contest.contestGameRewards.length !== 0"
              class="flex flex-col gap-8 border-t pb-6 pt-6"
            >
              <div v-for="(contestGameReward, index) in contest.contestGameRewards" :key="contestGameReward.name">
                <p class="mb-2">
                  <span class="text-sm"
                    >{{ t('pages.contests.contestsCreatePage.style.form.fields.rewardsColors.color') }} :</span
                  >
                  <span class="text-md ml-1 font-medium underline">{{ contestGameReward.name }}</span>
                </p>
                <div class="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                  <div class="flex-1">
                    <p class="text-sm text-foreground">
                      {{ t('pages.contests.contestsCreatePage.style.form.fields.rewardsColors.background') }}
                    </p>
                    <FormColorPicker :id="`gameReward.${index}.bgColor`" v-model="contestGameReward.color" />
                  </div>
                  <div class="flex-1">
                    <p class="text-sm text-foreground">
                      {{ t('pages.contests.contestsCreatePage.style.form.fields.rewardsColors.text') }}
                    </p>

                    <FormColorPicker :id="`gameReward.${index}.textColor`" v-model="contestGameReward.textColor" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </BaseDisclosure>
      </div>

      <div class="mt-5">
        <BaseDisclosure
          :title="t('pages.contests.contestsCreatePage.preview.disclosure.texts')"
          panel-class="!rounded-xl"
        >
          <div class="rounded-b-lg border bg-white px-2 py-8 sm:px-8">
            <div class="flex flex-col sm:items-start">
              <label for="title" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.form.fields.title') }}
              </label>
              <div class="mt-1 w-full">
                <AppFormInput id="title" v-model="contest.title" type="text" name="title" />
              </div>
            </div>

            <div class="flex flex-col pt-6 sm:items-start">
              <label for="subtitle" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.form.fields.subtitle') }}
              </label>
              <div class="mt-1 w-full">
                <AppFormInput id="subtitle" v-model="contest.subtitle" type="text" name="subtitle" />
              </div>
            </div>

            <div class="flex flex-col py-8 sm:items-start">
              <label for="callToActionText" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.form.fields.callToActionText') }}
              </label>
              <div class="mt-1 w-full">
                <AppFormInput
                  id="callToActionText"
                  v-model="contest.callToActionText"
                  type="text"
                  name="callToActionText"
                />
              </div>
            </div>

            <div class="flex flex-col border-t pt-8 sm:items-start">
              <label for="formTitle" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.form.fields.formTitle') }}
              </label>
              <div class="mt-1 w-full">
                <AppFormInput id="formTitle" v-model="contest.formTitle" type="text" name="formTitle" />
              </div>
            </div>

            <div class="flex flex-col pt-6 sm:items-start">
              <label for="formSubtitle" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.form.fields.formSubTitle') }}
              </label>
              <div class="mt-1 w-full">
                <AppFormInput id="formSubtitle" v-model="contest.formSubtitle" type="text" name="formSubtitle" />
              </div>
            </div>

            <div class="flex flex-col pt-6 sm:items-start">
              <label for="formSubmitButtonText" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.form.fields.formSubmitButtonText') }}
              </label>
              <div class="mt-1 w-full">
                <AppFormInput
                  id="formSubmitButtonText"
                  v-model="contest.formSubmitButtonText"
                  type="text"
                  name="formSubmitButtonText"
                />
              </div>
            </div>

            <div class="flex flex-col py-8 sm:items-start">
              <label for="optinText" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.form.fields.optinText') }}
              </label>
              <div class="mt-1 w-full">
                <AppFormInput id="optinText" v-model="contest.optinText" type="text" name="optinText" />
              </div>
            </div>

            <div v-if="selectedBoost?.id != 'none'" class="flex flex-col border-t pb-6 pt-8 sm:items-start">
              <label for="askActionTitle" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.actionWindow.form.fields.askActionTitle') }}
              </label>
              <div class="mt-1 w-full sm:mt-0">
                <AppFormInput id="askActionTitle" v-model="contest.askActionTitle" type="text" name="askActionTitle" />
              </div>
            </div>

            <div v-if="selectedBoost?.id === 'custom'" class="flex flex-col pb-6 sm:items-start">
              <label for="askActionText" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.actionWindow.form.fields.askActionText') }}
              </label>
              <div class="mt-1 w-full sm:mt-0">
                <AppFormInput id="askActionText" v-model="contest.askActionText" type="text" name="askActionText" />
              </div>
            </div>
            <div v-if="selectedBoost?.id != 'none'" class="flex flex-col pb-6 sm:items-start">
              <label for="askActionButtonText" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.actionWindow.form.fields.askActionButtonText') }}
              </label>
              <div class="mt-1 w-full sm:mt-0">
                <AppFormInput
                  id="askActionButtonText"
                  v-model="contest.askActionButtonText"
                  type="text"
                  name="askActionButtonText"
                />
              </div>
            </div>
            <div class="flex flex-col pb-6 sm:items-start">
              <label for="winningText" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.form.fields.winningText') }}
              </label>
              <div class="mt-1 w-full sm:mt-0">
                <AppFormInput id="winningText" v-model="contest.winningText" type="text" name="winningText" />
              </div>
            </div>
            <div class="flex flex-col pb-6 sm:items-start">
              <label for="losingText" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.style.form.fields.losingText') }}
              </label>
              <div class="mt-1 w-full sm:mt-0">
                <AppFormInput id="losingText" v-model="contest.losingText" type="text" name="losingText" />
              </div>
            </div>
            <div class="flex flex-col sm:items-start">
              <label for="sendingNotificationText" class="block text-sm font-medium text-foreground">
                {{ t('pages.contests.contestsCreatePage.message.form.fields.confirmationText.label') }}
              </label>
              <div class="mt-1 w-full sm:mt-0">
                <AppFormInput
                  id="askActionText"
                  v-model="contest.sendingNotificationText"
                  type="text"
                  name="sendingNotificationText"
                />
              </div>
            </div>
          </div>
        </BaseDisclosure>
      </div>
    </div>

    <ContestsPreview :key="contest.logo" :contest="contest as Contest" :mechanic="mechanic" :selected-boost="noBoost" />
  </div>
</template>

<style>
.wrap-brick-dropdown button,
.wrap-brick-dropdown button div {
  width: 100% !important;
}
</style>
